import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import useBaseline from 'hooks/useBaseline';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { increaseLabel } from 'helper/remunerationDraftChange';

const DRAFT_KEY = 'sundayHours';

const ModalSundayHoursCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [percentage, setPercentage] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const baseline = useBaseline('');
  const token = localStorage.getItem('xsrfToken');
  // The page's changes held back (JBWEB-1468): this one joins them instead of
  // asking for its own justificatif.
  const draft = modalData?.draft;
  const staged = draft?.pending(DRAFT_KEY);

  useEffect(() => {
    if (!modalData?.idCompany) return;
    ApiDatabase.getSundayHoursCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        // The modal opens with the current percentage pre-filled: it serves
        // for the initial entry as well as for edits (RG-1).
        const current = data?.sundayHours?.percentage;
        const loaded =
          current === undefined || current === null ? '' : String(current);
        setPercentage(staged ?? loaded);
        baseline.capture(loaded);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [modalData?.idCompany]);

  const validate = (value) => {
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return 'Saisissez un nombre.';
    if (parsed < 0 || parsed > 100)
      return 'La majoration doit être comprise entre 0 et 100.';
    if (Math.round(parsed * 10) / 10 !== parsed)
      return 'La majoration accepte au plus une décimale.';
    return null;
  };

  const handleSubmit = () => {
    const invalid = validate(percentage);
    if (invalid) return setError(invalid);

    // These values reach contracts and invoices, so the change carries the
    // client's written request for it before anything is written.
    // Nothing to confirm when nothing moved: the confirmation asks for a
    // justificatif, and there would be no change for it to justify.
    if (draft) {
      if (!baseline.hasChanged(percentage)) draft.remove(DRAFT_KEY);
      else {
        const sundayHours = { percentage: parseFloat(String(percentage).replace(',', '.')) };
        draft.stage({
          key: DRAFT_KEY,
          section: DRAFT_KEY,
          label: 'Heures du dimanche',
          before: increaseLabel(baseline.value()),
          after: increaseLabel(percentage),
          form: percentage,
          preview: { type: 'sunday_hours_updated', sundayHours },
          commit: send,
        });
      }
      return toggle();
    }
    if (!baseline.hasChanged(percentage)) return;
    if (!justificatif.isConfirming) return justificatif.setIsConfirming(true);
    justificatif.confirm(save);
  };

  // The save, as the modal return it resolves with: run here, or later with
  // the page's other changes.
  const send = (storedJustificatif) =>
    new Promise((resolve, reject) =>
      ApiDatabase.postSundayHoursCompany(
        {
          token,
          justificatif: storedJustificatif,
          idCompany: modalData.idCompany,
          percentage: parseFloat(String(percentage).replace(',', '.')),
        },
        (data) => resolve({ type: 'sunday_hours_updated', sundayHours: data.sundayHours }),
        reject,
      ),
    );

  const save = (storedJustificatif) => {
    setIsLoading(true);
    send(storedJustificatif).then(
      (saved) => {
        setIsLoading(false);
        toast.success('La majoration des heures du dimanche a été enregistrée');
        setModalDataReturn(saved);
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // The modal stays open, the input intact (RG-6).
        setError(err?.response?.data?.message ?? "L'enregistrement a échoué");
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Heures du dimanche'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
          />
        ) : (
          <>
        <div className={'flex flex-col gap-3 text-sm'}>
          <div className={'text-gray-500'}>
            Les majorations que vous définissez ici s’appliquent à toutes les
            heures travaillées les dimanches.
          </div>
          <label className={'flex flex-col gap-1'}>
            Majoration
            <div className={'flex items-center gap-2'}>
              <input
                type={'text'}
                className={`border rounded px-2 py-2 w-full ${error ? 'border-red-500' : ''}`}
                value={percentage}
                onChange={(e) => {
                  setPercentage(e.target.value);
                  setError(null);
                }}
              />
              <span className={'text-gray-500'}>%</span>
            </div>
          </label>
          {error && <div className={'text-red-500 text-xs'}>{error}</div>}
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
                  size={'LG'}
                  color={'SECONDARY'}
                  // Backs out of the confirmation, not the modal: the values
                  // being confirmed are still worth seeing.
                  onClick={
                    justificatif.isConfirming
                      ? () => justificatif.setIsConfirming(false)
                      : toggle
                  }
                >
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={
            isLoading ||
            justificatif.isUploading ||
            ((!baseline.hasChanged(percentage) && staged === undefined) ||
              (justificatif.isConfirming && !justificatif.canConfirm))
              ? 'DISABLED'
              : 'PRIMARY'
          }
          onClick={
            isLoading || justificatif.isUploading ? undefined : handleSubmit
          }
        >
          {isLoading || justificatif.isUploading
            ? 'Confirmation ...'
            : draft
              ? 'Ajouter aux modifications'
              : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalSundayHoursCompany;
